import React, { useCallback, useRef, useContext, useEffect, memo } from 'react';

import { useIsFhsEnabled } from '@atlaskit/navigation-system/fhs-rollout';
import { UFOThirdPartySegment } from '@atlaskit/react-ufo/segment';

import type { BannerStateContainer } from '@confluence/banners/entry-points/BannerStateContainer.ts';
import { type FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import {
	DATABASE_CONTENT_EXPERIENCE,
	DATABASE_LOAD_HISTORICAL_VERSION,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { useObjectSidebarState } from '@confluence/object-sidebar-api/entry-points/useObjectSidebar.ts';
import type { ContentTypesObjectSidebarService } from '@confluence/content-types-bridge/entry-points/content-types-object-sidebar-service.ts';
import { BannerContext } from '@confluence/banners/entry-points/BannerContext.ts';
import { useIsMobileApp } from '@confluence/mobile-app-detection/entry-points/MobileAppDetection.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { DATABASE_IFRAME_TITLE } from '../database-constants.ts';
import { DATABASE_VIEW_PAGE_CONTENT_SEGMENT } from '../perf.config.tsx';
import { databaseShellStyle } from '../database-shell-styles.ts';
import { useDatabaseBridgeData } from '../hooks/useDatabaseBridgeData.ts';
import { useDatabaseBridgeMethods } from '../hooks/useDatabaseBridgeMethods.ts';
import { usePostMessageHandler } from '../hooks/index.ts';
import { useDatabaseParams } from '../database-params.ts';
import type { DatabaseIdentity } from '../database-shell-types.ts';
import type { DatabaseOnboardingService } from '../services/database-onboarding-service.ts';
import type { DatabaseVersionHistoryTypes } from '../hooks/useDatabaseVersionHistory.ts';
import { parseRovoReceiverId } from '../utils/parse-rovo-receiver-id.ts';

// Match the heights rendered by navigation-system for the active FHS rollout state.
const DEFAULT_TOP_NAV_HEIGHT = 48;
const FHS_TOP_NAV_HEIGHT = 56;

interface DatabaseIframeProps {
	contentId: string;
	contentReferenceId?: string | null;
	onboardingService: DatabaseOnboardingService;
	spaceKey?: string;
	dataSecurityPolicyPageExport: boolean | null;
	dataSecurityPolicyAttachmentDownload: boolean | null;
	identity?: DatabaseIdentity | null;
	refetch?: () => void;
	spaceId?: string | null;
	spaceName?: string | null;
	title?: string | null;
	loading: boolean;
	isSmartLinkEmbed: boolean;
	headerHeight: number;
	bannerState: BannerStateContainer;
	isSidePanelOpen?: boolean;
	dbVersionHistory: DatabaseVersionHistoryTypes;
	objectSidebarService: ContentTypesObjectSidebarService;
	flags: FlagsStateContainer;
	isRovoParentProduct: boolean;
}

export const DatabaseIframe: React.MemoExoticComponent<
	({
		contentId,
		contentReferenceId,
		onboardingService,
		spaceKey,
		dataSecurityPolicyPageExport,
		dataSecurityPolicyAttachmentDownload,
		identity,
		refetch,
		spaceId,
		spaceName,
		title,
		loading,
		isSmartLinkEmbed,
		headerHeight,
		bannerState,
		isSidePanelOpen,
		dbVersionHistory,
		objectSidebarService,
		flags,
		isRovoParentProduct,
	}: DatabaseIframeProps) => React.JSX.Element
> = memo(
	({
		contentId,
		contentReferenceId,
		onboardingService,
		spaceKey,
		dataSecurityPolicyPageExport,
		dataSecurityPolicyAttachmentDownload,
		identity,
		refetch,
		spaceId,
		spaceName,
		title,
		loading,
		isSmartLinkEmbed,
		headerHeight,
		bannerState,
		isSidePanelOpen,
		dbVersionHistory,
		objectSidebarService,
		flags,
		isRovoParentProduct,
	}: DatabaseIframeProps): React.JSX.Element => {
		const iframeRef = useRef<HTMLIFrameElement | null>(null);
		const databaseSpotlightCloseRef = useRef<(() => void) | null>(null);
		usePostMessageHandler(iframeRef);
		const { orgId } = useSessionData();
		const { isObjectSidebarShown } = useObjectSidebarState();
		const { includeTopNav } = useContext(BannerContext);
		const { isMobileApp } = useIsMobileApp();
		const isFhsEnabled = useIsFhsEnabled();
		const isStandardConfluenceView = !isSmartLinkEmbed && !isMobileApp;
		const topNavHeight = includeTopNav
			? isFhsEnabled
				? FHS_TOP_NAV_HEIGHT
				: DEFAULT_TOP_NAV_HEIGHT
			: 0;
		const iframeHeightOffset = isStandardConfluenceView
			? bannerState.getTotalHeight(false) + headerHeight + topNavHeight
			: 0;

		const experienceTracker = useContext(ExperienceTrackerContext);
		const params = useDatabaseParams();

		// Parse bridgeClient to extract dbClientId for Rovo receiver
		const rovoReceiverId = parseRovoReceiverId(params.bridgeClient);

		const databaseVersionId =
			isSidePanelOpen && !dbVersionHistory.versionId ? 'latest' : dbVersionHistory.versionId;

		const { initialUrl, bridgeData } = useDatabaseBridgeData({
			contentId,
			contentReferenceId,
			spaceKey,
			dataSecurityPolicyPageExport,
			dataSecurityPolicyAttachmentDownload,
			identity,
			refetch,
			spaceId,
			spaceName,
			title,
			version: params['databases-frontend'],
			isSmartLinkEmbed,
			versionId: databaseVersionId,
			presentationMode: params.presentationMode,
			isNativeEmbedFullscreen: params.isNativeEmbedFullscreen,
			bridgeClient: params.bridgeClient,
			isRovoParentProduct,
		});

		const handleDatabaseSpotlightClose = (event: MouseEvent) => {
			if (
				iframeRef?.current &&
				!iframeRef.current.contains(event.target as Node) &&
				databaseSpotlightCloseRef.current
			) {
				databaseSpotlightCloseRef.current();
			}
		};

		const bridgeMethods = useDatabaseBridgeMethods({
			contentId,
			experienceTracker,
			onboardingService,
			orgId,
			segmentMetric: DATABASE_VIEW_PAGE_CONTENT_SEGMENT,
			experienceName: DATABASE_CONTENT_EXPERIENCE,
			versionHistoryExperienceName: DATABASE_LOAD_HISTORICAL_VERSION,
			versionId: databaseVersionId,
			getVersionName: dbVersionHistory.getVersionName,
			objectSidebarService,
			databaseSpotlightCloseRef,
			flags,
		});
		// @ts-expect-error -- This is a temporary solution to enable migration to the new bridge API
		window.contentAPI = bridgeMethods;

		const onIframeLoaded = useCallback(
			(e: React.SyntheticEvent<HTMLIFrameElement>) => {
				const iframe = e.target as HTMLIFrameElement;
				if (iframe) {
					iframeRef.current = iframe;
					if (iframe.contentWindow) {
						iframe.contentWindow.databaseBridge = bridgeMethods;

						iframe.contentWindow.postMessage(
							{
								type: 'bridge-ready',
							},
							'*',
						);
					}
				}
			},
			[bridgeMethods],
		);

		// On page refresh, the iframe content can load from browser cache before
		// React attaches the onLoad handler. Since the load event only fires once,
		// onIframeLoaded never runs and the bridge is never set, causing duplicate
		// meatball menus. This effect acts as a fallback to set the bridge if onLoad
		// was missed.
		useEffect(() => {
			const iframe = iframeRef.current;
			if (iframe?.contentWindow && !iframe.contentWindow.databaseBridge) {
				iframe.contentWindow.databaseBridge = bridgeMethods;
				const targetOrigin = new URL(iframe.src).origin;
				iframe.contentWindow.postMessage({ type: 'bridge-ready' }, targetOrigin);
			}
		}, [bridgeMethods]);

		useEffect(() => {
			if (databaseVersionId) {
				experienceTracker.start({ name: DATABASE_LOAD_HISTORICAL_VERSION });
			}
		}, [databaseVersionId, experienceTracker]);

		// Track clicks outside of the DB iframe to properly close database spotlights within the iframe
		useEffect(() => {
			document.addEventListener('mousedown', handleDatabaseSpotlightClose);

			return () => document.removeEventListener('mousedown', handleDatabaseSpotlightClose);
		});

		return (
			<UFOThirdPartySegment name="confluence-database-iframe">
				<iframe
					ref={iframeRef}
					// Every time we update iframe src attribute directly we push to the history stack which breaks the back/forward button
					// To prevent this, we pass in a key attribute to ensure that we remount the iframe each time to avoid adding to history stack
					key={initialUrl}
					src={initialUrl}
					title={DATABASE_IFRAME_TITLE}
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					style={databaseShellStyle(iframeHeightOffset, isSidePanelOpen, isObjectSidebarShown)}
					data-bridge-data={!loading ? JSON.stringify(bridgeData) : undefined}
					data-rovo-receiver-id={rovoReceiverId}
					onLoad={onIframeLoaded}
				/>
			</UFOThirdPartySegment>
		);
	},
);
